import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  formatCompactCurrencyAmount,
  formatCurrencyAmount,
  NotFoundError,
} from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { Suspense, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { z } from "zod";
import { CurrencyRateUI } from "~/components/Amount/CurrencyRateUI";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { graphql } from "~/gql/client";
import type { Currency } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/products_/$productId/unit-prices",
)({
  head: () => opsHead("Product Unit Prices"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const UtcDateTimeUI = ({ at }: { at: Date }) => {
  const utcDateTime = DateTime.fromJSDate(at).setZone("UTC");

  return (
    <div className="flex-1 space-x-1 text-sm">
      <div className="inline-flex items-baseline font-semibold text-sm">
        {utcDateTime.toFormat("yyyy-MM-dd")}
      </div>
      <div className="inline-flex items-baseline text-sm">
        {utcDateTime.toFormat("HH:mm")}
      </div>
      <div className="inline-flex items-baseline text-xs">
        {utcDateTime.toFormat("ZZZZ")}
      </div>
    </div>
  );
};

type UnitPriceHistoryItem = {
  at: Date;
  price: Decimal;
};

type UnitPriceHistoryChartDatum = {
  at: Date;
  dateLabel: string;
  price: number;
  priceLabel: string;
};

const CHART_HISTORY_MONTHS = 12;

const getColumns = (currency: Currency): ColumnDef<UnitPriceHistoryItem>[] => [
  {
    id: "at",
    accessorFn: (row) => row.at.getTime(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="As of" />
    ),
    cell: ({ row }) => <UtcDateTimeUI at={row.original.at} />,
  },
  {
    id: "price",
    accessorFn: (row) => row.price.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Value" />
    ),
    cell: ({ row }) => (
      <CurrencyRateUI currency={currency} amount={row.original.price} />
    ),
  },
];

const updateUnitPricesFormSchema = z.object({
  unitPriceFile: z
    .instanceof(File, {
      message: "Unit price file is required.",
    })
    .optional(),
  clearExistingUnitPrices: z.boolean().default(false),
});

type UpdateUnitPricesFormValues = z.input<typeof updateUnitPricesFormSchema>;

const unitPriceFileUrlFormSchema = z.object({
  unitPriceFileUrl: z
    .string()
    .trim()
    .url("Must be a valid URL.")
    .or(z.literal("")),
});

type UnitPriceFileUrlFormValues = z.input<typeof unitPriceFileUrlFormSchema>;

const fileToBase64 = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

function UnitPriceFileUrlEditor({
  productId,
  currentUrl,
}: {
  productId: string;
  currentUrl: string | null;
}) {
  const [setFundUnitPriceFileUrl, { loading }] = useMutation(
    graphql(`
      mutation AdminSetFundUnitPriceFileUrl(
        $input: SetFundUnitPriceFileUrlInput!
      ) {
        setFundUnitPriceFileUrl(input: $input) {
          fundProduct {
            id
            unit {
              id
              unitPriceFileUrl
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Unit price file URL updated",
        });
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to update unit price file URL",
          description:
            error.graphQLErrors?.[0]?.message ?? error.message ?? undefined,
        });
      },
    },
  );

  const form = useAppForm({
    defaultValues: {
      unitPriceFileUrl: currentUrl ?? "",
    } as UnitPriceFileUrlFormValues,
    validators: {
      onSubmit: unitPriceFileUrlFormSchema,
    },
    onSubmit: async ({ value }) => {
      await setFundUnitPriceFileUrl({
        variables: {
          input: {
            productId,
            unitPriceFileUrl: value.unitPriceFileUrl.trim() || null,
          },
        },
      });
    },
  });

  return (
    <div className="flex flex-col gap-4 pt-4">
      {!currentUrl && (
        <Alert
          kind="warning"
          title="Automatic updates disabled"
          description="Unit prices for this product are not being automatically updated. Set a unit price file URL below to enable automatic updates."
        />
      )}
      <form.AppForm>
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <form.AppField name="unitPriceFileUrl">
                {(field) => (
                  <field.InputField
                    label="Unit price file URL"
                    description="Link to the source unit price file. Leave blank to clear."
                    placeholder="https://example.com/unit-prices.xlsx"
                    type="url"
                  />
                )}
              </form.AppField>
            </div>
            <form.SubmitButton
              className="mt-6"
              childrenWhileSubmitting="Saving..."
              disabled={loading}
            >
              Save
            </form.SubmitButton>
          </div>
          {currentUrl && (
            <a
              href={currentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-info text-sm hover:underline"
            >
              Download current unit price file
            </a>
          )}
        </form>
      </form.AppForm>
    </div>
  );
}

function UploadUnitPricesDialog({ productId }: { productId: string }) {
  const [open, setOpen] = useState(false);

  const [updateFundUnitPrices] = useMutation(
    graphql(`
      mutation AdminUpdateFundUnitPrices($input: UpdateFundUnitPricesInput!) {
        updateFundUnitPrices(input: $input) {
          fundProduct {
            id
          }
        }
      }
    `),
    {
      refetchQueries: ["AdminProductUnitPrices"],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Unit prices updated",
        });
        setOpen(false);
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to update unit prices",
          description:
            error.graphQLErrors?.[0]?.message ?? error.message ?? undefined,
        });
      },
    },
  );

  const form = useAppForm({
    defaultValues: {
      unitPriceFile: undefined,
      clearExistingUnitPrices: false,
    } as UpdateUnitPricesFormValues,
    validators: {
      onSubmit: updateUnitPricesFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (!value.unitPriceFile) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Unit price file is required",
        });
        return;
      }

      const unitPriceFileBase64 = await fileToBase64(value.unitPriceFile);

      await updateFundUnitPrices({
        variables: {
          input: {
            productId,
            clearExistingUnitPrices: value.clearExistingUnitPrices ?? false,
            unitPriceFileBase64,
          },
        },
      });
    },
  });

  const onOpenChange = (next: boolean) => {
    if (!next) {
      form.reset();
    }
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="primary" size="sm">
          Upload data
        </Button>
      </DialogTrigger>
      <DialogContent requireIntentionalClosing={true}>
        <DialogHeader>
          <DialogTitle>
            <Txt variant="card-title">Upload unit price data</Txt>
          </DialogTitle>
        </DialogHeader>
        <form.AppForm>
          <form
            className="space-y-4"
            encType="multipart/form-data"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.AppField name="unitPriceFile">
              {(field) => (
                <div className="flex flex-col space-y-2">
                  <Label htmlFor="unitPriceFile">Unit price data file</Label>
                  <Input
                    id="unitPriceFile"
                    type="file"
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      field.handleChange(file ?? undefined);
                    }}
                  />
                </div>
              )}
            </form.AppField>
            <form.AppField name="clearExistingUnitPrices">
              {(field) => <field.SwitchField label="Overwrite existing data" />}
            </form.AppField>
            <div className="flex justify-end">
              <form.SubmitButton
                childrenWhileSubmitting="Uploading..."
                disabled={form.state.isSubmitting}
              >
                Upload
              </form.SubmitButton>
            </div>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function View() {
  const params = useParams({ strict: false });
  const productId = params.productId ?? "";

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminProductUnitPrices($productId: ID!) {
        product(id: $productId) {
          id
          ... on FundProduct {
            unit {
              id
              unitPriceFileUrl
            }
            unitPriceHistory {
              id
              currency
              unitPrices {
                targetUnit
                at
                price
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        productId,
      },
    },
  );

  const fundProduct =
    data.product?.__typename === "FundProduct" ? data.product : null;

  if (!fundProduct) {
    throw new NotFoundError();
  }

  const unitPriceHistory = fundProduct.unitPriceHistory;
  const currency = unitPriceHistory?.currency ?? null;

  const unitPriceFileUrl = fundProduct.unit?.unitPriceFileUrl ?? null;

  const unitPriceHistoryItems: UnitPriceHistoryItem[] = (
    unitPriceHistory?.unitPrices ?? []
  )
    .map((item) => ({
      at: item.at,
      price: new Decimal(item.price),
    }))
    .sort((a, b) => b.at.getTime() - a.at.getTime());
  const latestUnitPrice = unitPriceHistoryItems[0];

  const chartWindowStart = latestUnitPrice
    ? DateTime.fromJSDate(latestUnitPrice.at)
        .minus({ months: CHART_HISTORY_MONTHS })
        .toMillis()
    : 0;
  const chartData: UnitPriceHistoryChartDatum[] =
    latestUnitPrice && currency
      ? unitPriceHistoryItems
          .filter((item) => item.at.getTime() >= chartWindowStart)
          .sort((a, b) => a.at.getTime() - b.at.getTime())
          .map((item) => ({
            at: item.at,
            dateLabel: DateTime.fromJSDate(item.at)
              .setZone("UTC")
              .toFormat("d MMM yy"),
            price: item.price.toNumber(),
            priceLabel: formatCurrencyAmount({
              currency,
              amount: item.price,
              notation: "standard",
            }),
          }))
      : [];

  const chartConfig = {
    price: {
      label: "Unit price",
      color: "hsl(var(--color-info-600))",
    },
  } satisfies ChartConfig;

  return (
    <div className="flex flex-col gap-6">
      <Card variant="panel">
        <CardHeader>
          <CardTitle>
            <Txt variant="card-title">Details</Txt>
          </CardTitle>
        </CardHeader>
        <CardContent className="w-full">
          <DetailRow
            label="Latest price"
            value={
              latestUnitPrice && currency ? (
                <CurrencyRateUI
                  currency={currency}
                  amount={latestUnitPrice.price}
                />
              ) : (
                "No price data"
              )
            }
          />
          <DetailRow
            label="As of"
            value={
              latestUnitPrice ? <UtcDateTimeUI at={latestUnitPrice.at} /> : "-"
            }
          />
          <UnitPriceFileUrlEditor
            key={unitPriceFileUrl ?? "unset"}
            productId={productId}
            currentUrl={unitPriceFileUrl}
          />
        </CardContent>
      </Card>

      <Card variant="panel">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            <Txt variant="card-title">History</Txt>
          </CardTitle>
          <UploadUnitPricesDialog productId={productId} />
        </CardHeader>
        <CardContent className="w-full">
          {latestUnitPrice && currency ? (
            <Tabs defaultValue="chart">
              <TabsList size="xs" className="self-start">
                <TabsTrigger value="chart">Chart</TabsTrigger>
                <TabsTrigger value="table">Table</TabsTrigger>
              </TabsList>
              <TabsContent value="chart" className="mt-4">
                <div className="rounded-lg border p-4">
                  <div className="mb-4 text-secondary text-sm">
                    Showing the last {CHART_HISTORY_MONTHS} months of unit price
                    history.
                  </div>
                  <ChartContainer
                    config={chartConfig}
                    className="h-[280px] w-full min-w-0"
                  >
                    <LineChart data={chartData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--color-neutral-200))"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="dateLabel"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        minTickGap={24}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        width={96}
                        tickFormatter={(value) =>
                          formatCompactCurrencyAmount({
                            currency,
                            amount: new Decimal(value),
                            hideCurrencyCode: true,
                          })
                        }
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          const point = payload?.[0]?.payload as
                            | UnitPriceHistoryChartDatum
                            | undefined;

                          if (!active || !point) {
                            return null;
                          }

                          const pointDateTime = DateTime.fromJSDate(
                            point.at,
                          ).setZone("UTC");

                          return (
                            <div className="rounded-lg border bg-primary p-3 shadow-popover">
                              <div className="text-secondary text-xs">
                                {pointDateTime.toFormat(
                                  "yyyy-MM-dd HH:mm ZZZZ",
                                )}
                              </div>
                              <div className="font-medium text-primary text-sm">
                                {point.priceLabel}
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="price"
                        stroke={chartConfig.price.color}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </LineChart>
                  </ChartContainer>
                </div>
              </TabsContent>
              <TabsContent value="table" className="mt-4">
                <DataTable
                  data={unitPriceHistoryItems}
                  columns={getColumns(currency)}
                  initialState={{
                    pagination: {
                      pageSize: 20,
                    },
                  }}
                  columnSortProps={{
                    id: "at",
                    desc: true,
                  }}
                  className="overflow-hidden rounded-lg border"
                />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-secondary text-sm">
              No unit price history for this product.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
